import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IntegrationDetailHeader } from "@features/ix/shared/IntegrationDetailHeader";
import { toast } from "@shared/hooks/use-toast";
import { Tabs } from "@shared/ui/tabs";
import { PauseIntegrationDialog } from "@features/ix/shared/PauseIntegrationDialog";

import OverviewTab from "./tabs/overview/overview";
import SyncActivityTab from "./tabs/syncActivity/syncActivity";
import DiagnosticsTab from "./tabs/diagnostics/diagnostics";
import MonitoringTab from "./tabs/monitoring/monitoring";
import { VersionsTab } from "@features/ix/shared/VersionsTab";
import { useGetConnectionDetails } from "@features/ix/erpIntegration/hooks";

export const UsPaymentDetailLanding: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isSyncing, setIsSyncing] = useState(false);
  const [isPauseDialogOpen, setIsPauseDialogOpen] = useState(false);
  const { data: connectionDetails } = useGetConnectionDetails(id);

  // Data defined at the landing page level to be passed to the shared header
  const headerData = {
    title: connectionDetails?.name || "N/A",
    iconInitials: "Us",
    iconBgClass: "bg-[#0f766e]", // Teal theme matching modern styling
    status: connectionDetails?.statusLabel,
    version: connectionDetails?.latestVersion || "N/A",
    subtitlePrefix: "Payment Processor",
    stats: [
      { label: "Last sync", value: "0 min ago" },
      { label: "Synced accounts", value: "0" },
      { label: "Uptime", value: "0%", highlighted: true },
    ],
  };

  const handleSyncNow = () => {
    if (isSyncing) return;
    setIsSyncing(true);
    toast({
      title: "Sync Started",
      description:
        "Triggering a manual data synchronization with US Payment...",
    });

    setTimeout(() => {
      setIsSyncing(false);
      toast({
        title: "Sync Completed",
        description:
          "Successfully synchronized 14 new customer billing records.",
      });
    }, 2000);
  };

  const handleConfigure = () => {
    navigate(`/payments/uspayment/configure/${id}`);
  };

  const handlePauseConfirm = () => {
    setIsPauseDialogOpen(false);
    toast({
      title: "Integration Paused",
      description: "Data synchronization has been paused.",
    });
  };

  const tabComponents = {
    overview: {
      label: "Overview",
      component: <OverviewTab />,
    },
    syncLogs: {
      label: "Sync Logs",
      component: <SyncActivityTab />,
      count: 0,
    },
    errors: {
      label: "Errors",
      component: <DiagnosticsTab />,
      count: 0,
    },
    monitoring: {
      label: "Monitoring",
      component: <MonitoringTab />,
    },
    versions: {
      label: "Versions",
      component: <VersionsTab />,
      count: 0,
    },
  };

  const urlMapping = {
    overview: "overview",
    syncLogs: "sync-logs",
    errors: "errors",
    monitoring: "monitoring",
    versions: "versions",
  };

  return (
    <div className="space-y-6">
      {/* Shared Detail Header */}
      <IntegrationDetailHeader
        title={headerData.title}
        iconInitials={headerData.iconInitials}
        iconBgClass={headerData.iconBgClass}
        status={headerData.status}
        version={headerData.version}
        subtitlePrefix={headerData.subtitlePrefix}
        stats={headerData.stats}
        onRunNow={handleSyncNow}
        onPause={() => setIsPauseDialogOpen(true)}
        onConfigure={handleConfigure}
      />

      {/* Tabs */}
      <Tabs
        defaultValue="overview"
        className="w-full"
        tabsListClassName="grid grid-cols-5 w-full"
        urlMapping={urlMapping}
        tabComponents={tabComponents}
        level={0}
      />

      {/* Pause Dialogue */}
      <PauseIntegrationDialog
        isOpen={isPauseDialogOpen}
        onClose={() => setIsPauseDialogOpen(false)}
        onConfirm={handlePauseConfirm}
      />
    </div>
  );
};

export default UsPaymentDetailLanding;
